import * as THREE from "three";
import { sceneManager } from "../SetUps/SceneManager";
import { VARS } from "../Global/variables";
import { WEB_MERCATOR_TILE_SIZE } from "../../helpers/CONSTANT";

const MAX_MERCATOR_LAT = 85.05112878;
const EARTH_RADIUS_METERS = 6378137;
const GEO_TIFF_UV_OUTSIDE_EPS = 0.02;
const GEO_TIFF_UV_SOFT_MAX_OUTSIDE = 0.35;
const EARTH_METERS_PER_DEG_LAT = 110540;

const toFiniteNumber = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp01 = (value) => THREE.MathUtils.clamp(value, 0, 1);

const normalizeLongitude = (longitude) => {
  let normalized = toFiniteNumber(longitude, 0);
  while (normalized > 180) normalized -= 360;
  while (normalized < -180) normalized += 360;
  return normalized;
};

const projectLngToWorld = (longitude, worldSize) =>
  ((normalizeLongitude(longitude) + 180) / 360) * worldSize;

const projectLatToWorld = (latitude, worldSize) => {
  const clampedLat = THREE.MathUtils.clamp(
    toFiniteNumber(latitude, 0),
    -MAX_MERCATOR_LAT,
    MAX_MERCATOR_LAT
  );
  const latRad = THREE.MathUtils.degToRad(clampedLat);
  const mercator =
    0.5 -
    Math.log((1 + Math.sin(latRad)) / (1 - Math.sin(latRad))) / (4 * Math.PI);
  return mercator * worldSize;
};

const latLngToWebMercatorMeters = (latitude, longitude) => {
  const clampedLat = THREE.MathUtils.clamp(
    toFiniteNumber(latitude, 0),
    -MAX_MERCATOR_LAT,
    MAX_MERCATOR_LAT
  );
  const lng = toFiniteNumber(longitude, 0);
  const x = (lng * Math.PI * EARTH_RADIUS_METERS) / 180;
  const y = EARTH_RADIUS_METERS * Math.log(Math.tan(Math.PI / 4 + (clampedLat * Math.PI) / 360));
  return { x, y };
};

const getGeotiffCoordinateCandidates = (latitude, longitude, preferredSpace) => {
  const geographic = {
    coordX: toFiniteNumber(longitude, 0),
    coordY: toFiniteNumber(latitude, 0),
    space: "geographic",
  };
  const merc = (() => {
    const m = latLngToWebMercatorMeters(latitude, longitude);
    return { coordX: m.x, coordY: m.y, space: "web-mercator-meters" };
  })();

  const preferredIsMerc = preferredSpace === "web-mercator-meters";
  return preferredIsMerc ? [merc, geographic] : [geographic, merc];
};

const evaluateGeotiffUvRaw = (
  coordX,
  coordY,
  originX,
  originY,
  pixelSizeX,
  pixelSizeY,
  widthPx,
  heightPx
) => {
  const col = (coordX - originX) / pixelSizeX;
  const row = (coordY - originY) / pixelSizeY;
  const uRaw = col / Math.max(widthPx - 1, 1);
  const vRaw = 1 - row / Math.max(heightPx - 1, 1);
  return { uRaw, vRaw };
};

const geotiffOutsideScore = (uRaw, vRaw) => {
  if (!Number.isFinite(uRaw) || !Number.isFinite(vRaw)) return Number.POSITIVE_INFINITY;
  const left = Math.max(0, -uRaw);
  const right = Math.max(0, uRaw - 1);
  const bottom = Math.max(0, -vRaw);
  const top = Math.max(0, vRaw - 1);
  return Math.max(left, right, bottom, top);
};

const wrapWorldDelta = (delta, worldSize) => {
  let wrapped = delta;
  const halfWorld = worldSize * 0.5;
  if (wrapped > halfWorld) wrapped -= worldSize;
  if (wrapped < -halfWorld) wrapped += worldSize;
  return wrapped;
};

const metersPerDegreeLngAtLat = (latitude) => {
  const latRad = THREE.MathUtils.degToRad(toFiniteNumber(latitude, 0));
  return Math.max(1e-6, 111320 * Math.cos(latRad));
};

const getLongitudeSpan = (west, east) => {
  let span = normalizeLongitude(east) - normalizeLongitude(west);
  while (span < 0) span += 360;
  while (span >= 360) span -= 360;
  return span;
};

const toBoundsUvCandidates = (mapReference, latitude, longitude) => {
  const west = toFiniteNumber(mapReference?.west, null);
  const east = toFiniteNumber(mapReference?.east, null);
  const southRaw = toFiniteNumber(mapReference?.south, null);
  const northRaw = toFiniteNumber(mapReference?.north, null);
  if (west == null || east == null || southRaw == null || northRaw == null) return [];

  const south = Math.min(southRaw, northRaw);
  const north = Math.max(southRaw, northRaw);
  const spanLng = getLongitudeSpan(west, east);
  if (!Number.isFinite(spanLng) || spanLng <= 1e-12) return [];
  const spanLat = north - south;
  if (!Number.isFinite(spanLat) || Math.abs(spanLat) <= 1e-12) return [];

  let lngDelta = normalizeLongitude(longitude) - normalizeLongitude(west);
  if (lngDelta < 0) lngDelta += 360;
  const uRaw = lngDelta / spanLng;

  const lat = toFiniteNumber(latitude, south);
  const vRawLinear = (lat - south) / spanLat;

  const mercatorYNorth = projectLatToWorld(north, 1);
  const mercatorYSouth = projectLatToWorld(south, 1);
  const mercatorY = projectLatToWorld(lat, 1);
  const mercatorSpan = mercatorYSouth - mercatorYNorth;
  const vRawMercator =
    Math.abs(mercatorSpan) > 1e-12
      ? 1 - (mercatorY - mercatorYNorth) / mercatorSpan
      : vRawLinear;

  return [
    {
      uRaw,
      vRaw: vRawLinear,
      mode: "bounds-linear",
    },
    {
      uRaw,
      vRaw: vRawMercator,
      mode: "bounds-mercator",
    },
  ];
};

const getMapTileLocalBBox = (mapTile) => {
  if (!mapTile?.geometry) return null;
  mapTile.geometry.computeBoundingBox();
  return mapTile.geometry.boundingBox?.clone() || null;
};

const latLngToMapUV = (mapReference, latitude, longitude) => {
  if (!mapReference) return null;

  if (mapReference.mode === "geotiffAffine") {
    const widthPx = toFiniteNumber(mapReference.widthPx, null);
    const heightPx = toFiniteNumber(mapReference.heightPx, null);
    const originX = toFiniteNumber(
      mapReference.originX ?? mapReference.originLon,
      null
    );
    const originY = toFiniteNumber(
      mapReference.originY ?? mapReference.originLat,
      null
    );
    const pixelSizeX = toFiniteNumber(
      mapReference.pixelSizeX ?? mapReference.pixelSizeLon,
      null
    );
    const pixelSizeY = toFiniteNumber(
      mapReference.pixelSizeY ?? mapReference.pixelSizeLat,
      null
    );
    const coordinateSpace = String(mapReference.coordinateSpace || "geographic").toLowerCase();

    if (
      widthPx == null ||
      heightPx == null ||
      widthPx < 2 ||
      heightPx < 2 ||
      originX == null ||
      originY == null ||
      pixelSizeX == null ||
      pixelSizeY == null ||
      Math.abs(pixelSizeX) <= 1e-15 ||
      Math.abs(pixelSizeY) <= 1e-15
    ) {
      return null;
    }

    const coordCandidates = getGeotiffCoordinateCandidates(
      latitude,
      longitude,
      coordinateSpace
    );
    const signCandidates = [pixelSizeY, -pixelSizeY];

    let best = null;
    const registerBest = (raw, meta = {}) => {
      const outside = geotiffOutsideScore(raw.uRaw, raw.vRaw);
      if (!best || outside < best.outside) {
        best = {
          ...raw,
          outside,
          ...meta,
        };
      }
      if (outside <= GEO_TIFF_UV_OUTSIDE_EPS) {
        return {
          u: clamp01(raw.uRaw),
          v: clamp01(raw.vRaw),
        };
      }
      return null;
    };

    for (const candidate of coordCandidates) {
      for (const testPixelSizeY of signCandidates) {
        if (Math.abs(testPixelSizeY) <= 1e-15) continue;
        const raw = evaluateGeotiffUvRaw(
          candidate.coordX,
          candidate.coordY,
          originX,
          originY,
          pixelSizeX,
          testPixelSizeY,
          widthPx,
          heightPx
        );
        const accepted = registerBest(raw, {
          source: "affine",
          space: candidate.space,
          usedPixelSizeY: testPixelSizeY,
        });
        if (accepted) return accepted;
      }
    }

    const boundsCandidates = toBoundsUvCandidates(mapReference, latitude, longitude);
    for (const candidate of boundsCandidates) {
      const accepted = registerBest(
        { uRaw: candidate.uRaw, vRaw: candidate.vRaw },
        { source: candidate.mode }
      );
      if (accepted) return accepted;
    }

    if (best && best.outside <= GEO_TIFF_UV_SOFT_MAX_OUTSIDE) {
      return {
        u: clamp01(best.uRaw),
        v: clamp01(best.vRaw),
      };
    }

    return null;
  }

  if (mapReference.mode === "bbox") {
    const west = toFiniteNumber(mapReference.west, 0);
    const east = toFiniteNumber(mapReference.east, 0);
    const southRaw = toFiniteNumber(mapReference.south, 0);
    const northRaw = toFiniteNumber(mapReference.north, 0);
    const south = Math.min(southRaw, northRaw);
    const north = Math.max(southRaw, northRaw);

    let longitudeSpan = east - west;
    if (longitudeSpan <= 0) longitudeSpan += 360;
    if (longitudeSpan <= 0) return null;

    let lngDelta = normalizeLongitude(longitude) - normalizeLongitude(west);
    if (lngDelta < 0) lngDelta += 360;

    const u = clamp01(lngDelta / longitudeSpan);
    // Mapbox bbox imagery is WebMercator-projected, so latitude must be mapped
    // in Mercator-Y space (not linear lat interpolation).
    const mercatorYNorth = projectLatToWorld(north, 1);
    const mercatorYSouth = projectLatToWorld(south, 1);
    const mercatorY = projectLatToWorld(toFiniteNumber(latitude, south), 1);
    const mercatorSpan = mercatorYSouth - mercatorYNorth;
    // Keep north mapped to +Y on the plane (top of tile), matching centerZoom flow.
    const v = clamp01(1 - (mercatorY - mercatorYNorth) / (mercatorSpan || 1));
    return { u, v };
  }

  const centerLat = toFiniteNumber(mapReference.centerLat, null);
  const centerLng = toFiniteNumber(mapReference.centerLng, null);
  const zoom = toFiniteNumber(mapReference.zoom, null);
  const width = toFiniteNumber(mapReference.width, null);
  const height = toFiniteNumber(mapReference.height, null);

  if (
    centerLat == null ||
    centerLng == null ||
    zoom == null ||
    width == null ||
    height == null
  ) {
    return null;
  }

  const worldSize = WEB_MERCATOR_TILE_SIZE * Math.pow(2, zoom);
  const x = projectLngToWorld(longitude, worldSize);
  const y = projectLatToWorld(latitude, worldSize);
  const centerX = projectLngToWorld(centerLng, worldSize);
  const centerY = projectLatToWorld(centerLat, worldSize);

  const deltaX = wrapWorldDelta(x - centerX, worldSize);
  const deltaY = y - centerY;

  const u = 0.5 + deltaX / width;
  const v = 0.5 - deltaY / height;
  return { u: clamp01(u), v: clamp01(v) };
};

const latLngToApproxUvByCenterCoverage = (mapTile, mapReference, latitude, longitude) => {
  const centerLat = toFiniteNumber(
    mapReference?.requestCenterLat ?? mapReference?.centerLat,
    null
  );
  const centerLng = toFiniteNumber(
    mapReference?.requestCenterLng ?? mapReference?.centerLng,
    null
  );
  if (centerLat == null || centerLng == null) return null;

  const userCoverage = mapTile?.userData?.mapCoverageMeters || null;
  const fallbackDiameter = toFiniteNumber(mapReference?.requestedRadiusMeters, null);
  const widthMeters = toFiniteNumber(
    userCoverage?.widthMeters,
    fallbackDiameter != null ? fallbackDiameter * 2 : null
  );
  const heightMeters = toFiniteNumber(
    userCoverage?.heightMeters,
    fallbackDiameter != null ? fallbackDiameter * 2 : null
  );
  if (
    widthMeters == null ||
    heightMeters == null ||
    widthMeters <= 0 ||
    heightMeters <= 0
  ) {
    return null;
  }

  const east = (toFiniteNumber(longitude, centerLng) - centerLng) * metersPerDegreeLngAtLat(centerLat);
  const north = (toFiniteNumber(latitude, centerLat) - centerLat) * EARTH_METERS_PER_DEG_LAT;

  const uRaw = 0.5 + east / widthMeters;
  const vRaw = 0.5 + north / heightMeters;
  const outside = geotiffOutsideScore(uRaw, vRaw);
  if (!Number.isFinite(outside) || outside > 0.75) return null;

  return {
    u: clamp01(uRaw),
    v: clamp01(vRaw),
  };
};

export const projectLatLngToMapWorld = (mapTile, latitude, longitude) => {
  if (!mapTile) return null;
  const mapReference = mapTile.userData?.mapGeoReference || null;
  let uv = latLngToMapUV(mapReference, latitude, longitude);
  if (!uv && mapReference?.mode === "geotiffAffine") {
    uv = latLngToApproxUvByCenterCoverage(mapTile, mapReference, latitude, longitude);
  }
  const localBBox = getMapTileLocalBBox(mapTile);
  if (!uv || !localBBox) return null;

  const width = (localBBox.max.x - localBBox.min.x) || 1;
  const height = (localBBox.max.y - localBBox.min.y) || 1;
  const localX = localBBox.min.x + uv.u * width;
  const localY = localBBox.min.y + uv.v * height;
  const localPoint = new THREE.Vector3(localX, localY, 0);
  return mapTile.localToWorld(localPoint);
};

// Worker singleton
let lidarWorker = null;
let jobId = 0;
let detachLidarInteractions = null;
const LIDAR_MOVE_EPS = 1e-4;
const LIDAR_PANEL_OCCLUSION_EPS = 0.02;
const LIDAR_PANEL_OCCLUSION_KEY = "__lidarPanelOcclusion";

// Panels are deliberately drawn without depth testing everywhere else in the
// editor, so they remain legible over roofs, irradiance, and selection states.
// Re-enabling depth testing for LiDAR occlusion recreates the old "inside the
// translucent segment" rendering bug from panel copy/paste. Keep the material
// untouched and hide only panels that are covered from above by the LiDAR.
function setPanelLidarOcclusion(panel, enabled) {
  if (!panel?.isMesh) return;

  panel.userData = panel.userData || {};
  let state = panel.userData[LIDAR_PANEL_OCCLUSION_KEY];

  if (enabled) {
    if (!state) {
      state = { sourceVisible: panel.visible };
      panel.userData[LIDAR_PANEL_OCCLUSION_KEY] = state;
    }
    panel.visible = false;
    return;
  }

  if (state) {
    panel.visible = state.sourceVisible;
  }
  delete panel.userData[LIDAR_PANEL_OCCLUSION_KEY];
}

function restoreLidarPanelOcclusion(scene) {
  scene?.traverse?.((object) => {
    if (object?.isMesh && object.name === "panel") {
      setPanelLidarOcclusion(object, false);
    }
  });
}

function getLidarSurfaceHeightAtLocalPoint(terrainMesh, localPoint) {
  const geometry = terrainMesh?.geometry;
  const positions = geometry?.getAttribute?.("position");
  const meta = terrainMesh?.userData?.lidarMeta;
  const gridWidth = meta?.gridWidth;
  const gridHeight = meta?.gridHeight;
  if (!positions || gridWidth < 2 || gridHeight < 2) return null;

  const minX = meta.minX;
  const maxX = meta.maxX;
  const minY = meta.minY;
  const maxY = meta.maxY;
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  if (!Number.isFinite(spanX) || !Number.isFinite(spanY) || spanX <= 0 || spanY <= 0) {
    return null;
  }

  const x = (localPoint.x - minX) / spanX * (gridWidth - 1);
  const y = (localPoint.y - minY) / spanY * (gridHeight - 1);
  if (x < 0 || y < 0 || x > gridWidth - 1 || y > gridHeight - 1) return null;

  const i0 = Math.min(gridWidth - 2, Math.max(0, Math.floor(x)));
  const j0 = Math.min(gridHeight - 2, Math.max(0, Math.floor(y)));
  const i1 = i0 + 1;
  const j1 = j0 + 1;
  const u = THREE.MathUtils.clamp(x - i0, 0, 1);
  const v = THREE.MathUtils.clamp(y - j0, 0, 1);
  const zAt = (i, j) => positions.getZ(i * gridHeight + j);

  // The mesh uses these two triangles per grid cell. Match that topology rather
  // than bilinearly smoothing across the diagonal when deciding whether to turn
  // depth testing on for a panel.
  const z00 = zAt(i0, j0);
  const z10 = zAt(i1, j0);
  const z01 = zAt(i0, j1);
  const z11 = zAt(i1, j1);
  if (![z00, z10, z01, z11].every(Number.isFinite)) return null;

  return u + v <= 1
    ? z00 + (z10 - z00) * u + (z01 - z00) * v
    : z11 + (z01 - z11) * (1 - u) + (z10 - z11) * (1 - v);
}

function updateLidarPanelOcclusion(terrainMesh) {
  const scene = sceneManager.scene;
  if (!scene || !terrainMesh?.visible) return;

  terrainMesh.updateWorldMatrix(true, false);
  const panelPoint = new THREE.Vector3();
  const localPoint = new THREE.Vector3();

  scene.traverse((panel) => {
    if (!panel?.isMesh || panel.name !== "panel") return;

    panel.updateWorldMatrix(true, false);
    const position = panel.geometry?.getAttribute?.("position");
    if (!position?.count) {
      setPanelLidarOcclusion(panel, false);
      return;
    }

    // Test the panel's corners and any intervening vertices. This inexpensive
    // check decides whether the panel overlaps LiDAR from above; it deliberately
    // does not alter panel depth/render precedence.
    let coveredFromAbove = false;
    const sampleIndices = [0];
    const stride = Math.max(1, Math.floor(position.count / 4));
    for (let i = 0; i < position.count; i += stride) sampleIndices.push(i);

    for (const index of sampleIndices) {
      panelPoint.fromBufferAttribute(position, Math.min(index, position.count - 1));
      panelPoint.applyMatrix4(panel.matrixWorld);
      localPoint.copy(panelPoint);
      terrainMesh.worldToLocal(localPoint);
      const lidarHeight = getLidarSurfaceHeightAtLocalPoint(terrainMesh, localPoint);
      if (lidarHeight != null && lidarHeight > localPoint.z + LIDAR_PANEL_OCCLUSION_EPS) {
        coveredFromAbove = true;
        break;
      }
    }

    setPanelLidarOcclusion(panel, coveredFromAbove);
  });
}

function syncFlushBaseToCurrentTransform(obj) {
  if (!obj) return;
  obj.userData = obj.userData || {};
  obj.userData.__flushBase = {
    pos: obj.position.clone(),
    scale: obj.scale.clone(),
  };
  obj.userData.__flushRebase = false;
}

function dispatchLidarGeometryChanged(terrainMesh, arrowControl, reason = "drag-end") {
  if (typeof window === "undefined" || !terrainMesh) return;

  window.dispatchEvent(
    new CustomEvent("LIDAR_GEOMETRY_CHANGED", {
      detail: {
        reason,
        moved: true,
        position: {
          x: terrainMesh.position.x,
          y: terrainMesh.position.y,
          z: terrainMesh.position.z,
        },
        arrowPosition: arrowControl
          ? {
              x: arrowControl.position.x,
              y: arrowControl.position.y,
              z: arrowControl.position.z,
            }
          : null,
      },
    })
  );

  window.dispatchEvent(
    new CustomEvent("DESIGN_UNSAVED_CHANGE", {
      detail: {
        source: "lidar",
        reason,
      },
    })
  );
}

function cleanupLidarSceneArtifacts() {
  if (typeof detachLidarInteractions === "function") {
    try {
      detachLidarInteractions();
    } catch {}
    detachLidarInteractions = null;
  }

  const lidarMesh = sceneManager.scene?.getObjectByName("lidarTerrainMesh");
  if (lidarMesh) {
    restoreLidarPanelOcclusion(sceneManager.scene);
    sceneManager.scene.remove(lidarMesh);
    lidarMesh.geometry?.dispose?.();
    lidarMesh.material?.dispose?.();
  }

  const arrow = sceneManager.scene?.getObjectByName("lidarArrowControl");
  if (arrow) {
    sceneManager.scene.remove(arrow);
    arrow.traverse((child) => {
      child.geometry?.dispose?.();
      child.material?.dispose?.();
    });
  }

  const canvas = sceneManager.renderer?.domElement;
  if (canvas) canvas.style.cursor = "default";
}

function getWorker() {
  if (lidarWorker) return lidarWorker;
  lidarWorker = new Worker(
    new URL("./LidarMeshWorker.js", import.meta.url),
    { type: "module" }
  );
  return lidarWorker;
}

// World-space height percentile of a model's vertices. Used to reference the
// roof height robustly: bbox.max.z would catch a chimney/antenna spike, but the
// 95th percentile sits at the roof ridge and ignores those outliers. Vertices
// are strided to a budget so large GLBs stay cheap.
function getModelHeightPercentile(object, percentile = 0.95) {
  if (!object) return null;
  const zs = [];
  const v = new THREE.Vector3();
  object.updateWorldMatrix(true, true);
  object.traverse((child) => {
    const pos = child?.isMesh ? child.geometry?.attributes?.position : null;
    if (!pos || !pos.count) return;
    const stride = Math.max(1, Math.floor(pos.count / 20000));
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(child.matrixWorld);
      zs.push(v.z);
    }
  });
  if (!zs.length) return null;
  zs.sort((a, b) => a - b);
  const p = THREE.MathUtils.clamp(percentile, 0, 1);
  const idx = Math.min(zs.length - 1, Math.max(0, Math.round((zs.length - 1) * p)));
  return zs[idx];
}

export function buildLidarMesh(lidarData) {
  if (!lidarData?.points?.length) return null;

  const mapTile =
    VARS.mapTile || sceneManager.scene?.getObjectByName?.("mapTile") || null;
  const lidarCenterLat = toFiniteNumber(lidarData?.lat, null);
  const lidarCenterLng = toFiniteNumber(lidarData?.lon, null);
  const autoAnchorWorld =
    mapTile && lidarCenterLat != null && lidarCenterLng != null
      ? projectLatLngToMapWorld(mapTile, lidarCenterLat, lidarCenterLng)
      : null;

  let savedLidarPos = null;
  let savedLidarPositionSpace = null;
  // Fetch the saved LiDAR position from the DB and restore the full XYZ drag, so
  // both the horizontal move and the arrow's vertical offset survive reload. In
  // georeferenced mode the stored value is an offset from the map/roof anchor, so
  // the restore rides on top of the freshly recomputed anchor rather than pinning
  // an absolute world Z.
  try {
    if (VARS.Current_Project?.model_data) {
      const parsed = JSON.parse(VARS.Current_Project.model_data);
      const lidarState = parsed?.lidar || null;
      savedLidarPos = lidarState?.position || null;
      savedLidarPositionSpace =
        typeof lidarState?.positionSpace === "string"
          ? lidarState.positionSpace
          : null;
    }
  } catch (e) {
    console.warn("Failed to parse saved LiDAR position:", e);
  }

  // console.log("LIDAR POSITION:", savedLidarPos, "AUTO ANCHOR:", autoAnchorWorld);

  // CloudCompare-style smoothing controls, mirroring dsm_to_vtu.py. Kept LIGHT
  // on purpose: the crispness comes from gridding near native resolution, not
  // from smoothing. Python uses fillpits=5 / smooth_iter=30 at 0.125 m (≈0.7 m
  // diffusion length); at the app's ~0.18 m cells that's ~3 px / ~12 iters.
  //   FILL_PITS        -> grey-closing window that removes narrow roof dents
  //   SMOOTH_ITERATIONS-> Laplacian relaxation passes (the main smoother)
  //   RELAXATION       -> per-pass step size (gentle, like relaxation_factor=0.1)
  const FILL_PITS = 3;
  const SMOOTH_ITERATIONS = 12;
  const RELAXATION = 0.1;

  // PREFERRED PATH: a structured DSM grid (Google Solar). The raster is already
  // a clean top-surface grid, so we mesh it DIRECTLY at native topology and only
  // fill NoData + smooth -- no lossy "points -> coarse re-bin -> max" step that
  // produced spikes/blobs. Falls back to the point-cloud path for synthetic data.
  const dsmGrid =
    lidarData?.dsmGrid &&
    lidarData.dsmGrid.width > 1 &&
    lidarData.dsmGrid.height > 1 &&
    lidarData.dsmGrid.z?.length
      ? lidarData.dsmGrid
      : null;

  let sampledPoints = null;
  let GRID_RESOLUTION = 0;
  let SMOOTHING_RADIUS = 0;

  if (!dsmGrid) {
    // merge points from all tiles (same)
    const points = lidarData.points.flatMap((tile) => tile.points);
    if (!points.length) return null;

    // downsample to a working budget.
    const MAX_POINTS = 160000;
    sampledPoints =
      points.length > MAX_POINTS
        ? points.filter((_, i) => i % Math.ceil(points.length / MAX_POINTS) === 0)
        : points;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const p of sampledPoints) {
      if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) continue;
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }

    const spanX = Number.isFinite(minX) && Number.isFinite(maxX) ? Math.max(maxX - minX, 1) : 1;
    const spanY = Number.isFinite(minY) && Number.isFinite(maxY) ? Math.max(maxY - minY, 1) : 1;

    GRID_RESOLUTION = THREE.MathUtils.clamp(
      Math.round(Math.sqrt(sampledPoints.length) * 0.9),
      96,
      360
    );
    const avgGridStep = Math.max(spanX, spanY) / Math.max(GRID_RESOLUTION - 1, 1);
    SMOOTHING_RADIUS = THREE.MathUtils.clamp(avgGridStep * 1.4, 0.4, 3.0);
  }

  // remove existing mesh/arrow/listeners before rebuilding
  cleanupLidarSceneArtifacts();

  // Worker job
  const myJob = ++jobId;
  const worker = getWorker();

  if (dsmGrid) {
    worker.postMessage({
      gridSurface: {
        width: dsmGrid.width,
        height: dsmGrid.height,
        x: dsmGrid.x,
        y: dsmGrid.y,
        z: dsmGrid.z,
      },
      FILL_PITS,
      SMOOTH_ITERATIONS,
      RELAXATION,
    });
  } else {
    worker.postMessage({
      sampledPoints,
      GRID_RESOLUTION,
      SMOOTHING_RADIUS,
      FILL_PITS,
      SMOOTH_ITERATIONS,
      RELAXATION,
    });
  }

  worker.onmessage = (ev) => {
    if (myJob !== jobId) return; // ignore stale jobs

    const data = ev.data;
    if (!data?.ok) {
      console.error("LiDAR worker error:", data?.error);
      return;
    }

    const { positions, colors, indices, meta } = data;

    // Sample the smoothed mesh surface height near the building centre (mesh-local
    // (0,0) == the building centroid). We reference the ACTUAL displayed surface,
    // not the raw DSM. percentile 0.5 over a tight radius -> the centre height;
    // ~0.9 over a wider radius -> the roof RIDGE (the high points). The worker's
    // Laplacian smoothing FLATTENS the roof (lowers the ridge, raises the eaves)
    // without moving the centre, so a centre pin leaves a steep roof's ridge below
    // the model. Aligning the RIDGE on both surfaces fixes that.
    const measureMeshHeight = (radius, percentile) => {
      const zs = [];
      for (let i = 0; i < positions.length; i += 3) {
        if (
          Math.abs(positions[i]) <= radius &&
          Math.abs(positions[i + 1]) <= radius
        ) {
          zs.push(positions[i + 2]);
        }
      }
      if (!zs.length) return null;
      zs.sort((a, b) => a - b);
      const idx = Math.min(
        zs.length - 1,
        Math.max(0, Math.round((zs.length - 1) * percentile))
      );
      return zs[idx];
    };
    // Centre (median, 3 m) kept for the fallback + diagnostics; ridge (90th pct,
    // 7 m) is the primary reference. Widen once if the tight window found nothing.
    const meshCentreHeight =
      measureMeshHeight(3, 0.5) ?? measureMeshHeight(8, 0.5);
    const meshRidgeHeight =
      measureMeshHeight(7, 0.9) ?? measureMeshHeight(10, 0.9);

    // Build geometry/material EXACTLY like before
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.computeVertexNormals();

    // SOLID opaque relief: no transparency, so the directional light gives the
    // surface real 3-D shading instead of a washed-out, see-through look.
    const material = new THREE.MeshLambertMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      transparent: false,
      opacity: 1,
      wireframe: false,
    });

    const terrainMesh = new THREE.Mesh(geometry, material);
    terrainMesh.name = "lidarTerrainMesh";
    terrainMesh.renderOrder = 2;
    terrainMesh.userData.lidarMeta = {
      minX: meta.minX,
      maxX: meta.maxX,
      minY: meta.minY,
      maxY: meta.maxY,
      minZ: meta.minZ,
      maxZ: meta.maxZ,
      gridWidth: meta.gridWidth,
      gridHeight: meta.gridHeight,
      coordinateFrame: "local-meter-offsets",
      source: lidarData?.source || null,
      googleSolar: lidarData?.googleSolar || null,
    };

    // Remove any stale mesh/listeners if another build completed first
    cleanupLidarSceneArtifacts();

    // --- Arrow creation EXACTLY like before ---
    const createArrowControl = () => {
      const arrowGroup = new THREE.Group();
      arrowGroup.name = "lidarArrowControl";

      const shaftGeometry = new THREE.CylinderGeometry(2, 2, 10, 8);
      const shaftMaterial = new THREE.MeshBasicMaterial({
        color: 0xff6600,
        transparent: true,
        opacity: 0.8,
      });
      const shaft = new THREE.Mesh(shaftGeometry, shaftMaterial);
      shaft.name = "arrowShaft";
      shaft.rotation.x = Math.PI / 2;

      const headGeometry = new THREE.ConeGeometry(6, 20, 8);
      const headMaterial = new THREE.MeshBasicMaterial({
        color: 0xff3300,
        transparent: true,
        opacity: 0.9,
      });
      const head = new THREE.Mesh(headGeometry, headMaterial);
      head.name = "arrowHead";
      head.rotation.x = Math.PI / 2;
      head.position.z = 10;

      arrowGroup.add(shaft);
      arrowGroup.add(head);

      arrowGroup.position.set(meta.centerX, meta.centerY, meta.alignedMaxZ + 20);
      return arrowGroup;
    };

    const arrowControl = createArrowControl();

    const anchorX = autoAnchorWorld?.x || 0;
    const anchorY = autoAnchorWorld?.y || 0;

    // Vertical placement (XY left untouched / origin): read "what is there" at the
    // centroid (the DSM surface height at the building center) and drop the LiDAR
    // onto the house model so its centroid surface point meets the model's roof.
    let anchorZ = 0;
    const centroidHeight = toFiniteNumber(
      lidarData?.heightReference?.centroidHeight,
      null
    );
    const houseModel = sceneManager.scene?.getObjectByName?.("houseModel") || null;
    if (houseModel && centroidHeight != null) {
      // Roof reference: the model's surface height DIRECTLY ABOVE the building
      // centre (anchorX, anchorY) -- the same XY the DSM centroid probe measured.
      // Comparing the two at the SAME point removes the per-house model-vs-DSM
      // mismatch that a global 95th-percentile + fixed drop could never satisfy
      // (a tall tree/neighbour elsewhere in the model would inflate the percentile).
      let modelRoofZ = null;
      let modelRoofSource = "none";

      houseModel.updateWorldMatrix(true, true);
      const downRay = new THREE.Raycaster();
      downRay.set(
        new THREE.Vector3(anchorX, anchorY, 1e5),
        new THREE.Vector3(0, 0, -1)
      );
      const hits = downRay.intersectObject(houseModel, true);
      if (hits.length) {
        // Topmost surface the ray pierces at the centre = the roof over the centre.
        modelRoofZ = hits[0].point.z;
        modelRoofSource = "raycast-centre";
      }

      // Fallbacks if the ray missed (model XY offset from the anchor): the robust
      // 95th-percentile vertex, then the bbox top.
      if (modelRoofZ == null) {
        modelRoofZ = getModelHeightPercentile(houseModel, 0.95);
        if (modelRoofZ != null) modelRoofSource = "percentile-95";
      }
      if (modelRoofZ == null) {
        const modelBox = new THREE.Box3().setFromObject(houseModel);
        if (!modelBox.isEmpty()) {
          modelRoofZ = modelBox.max.z;
          modelRoofSource = "bbox-max";
        }
      }

      if (modelRoofZ != null) {
        // Default = centre pin (fallback when ridge sampling can't run).
        let modelTargetZ = modelRoofZ;
        let roofReferenceHeight =
          meshCentreHeight != null ? meshCentreHeight : centroidHeight;
        let placementMode = "centre-pin";
        let modelRidgeZ = null;

        // PRIMARY: ridge match. Smoothing flattens the roof, so pinning the centre
        // leaves a steep roof's ridge below the model ridge. Reference the RIDGE on
        // both surfaces -- the high points near the centre -- so the ridges meet.
        // This lifts steep roofs and barely moves flat ones (ridge ~= centre).
        if (modelRoofSource === "raycast-centre" && meshRidgeHeight != null) {
          // Sample the MODEL roof over a grid around the centre and take a high
          // percentile = the ridge. Clamp out hits far above the centre roof so a
          // tree/tall neighbour in the model can't inflate the ridge.
          const RIDGE_RADIUS_METERS = 7;
          const RIDGE_PERCENTILE = 0.9;
          const MAX_RIDGE_RISE_ABOVE_CENTRE = 4;
          const GRID_N = 5;

          const ridgeRay = new THREE.Raycaster();
          const down = new THREE.Vector3(0, 0, -1);
          const ceiling = modelRoofZ + MAX_RIDGE_RISE_ABOVE_CENTRE;
          const hitZs = [];
          for (let a = 0; a < GRID_N; a++) {
            for (let b = 0; b < GRID_N; b++) {
              const ox =
                anchorX + ((a / (GRID_N - 1)) * 2 - 1) * RIDGE_RADIUS_METERS;
              const oy =
                anchorY + ((b / (GRID_N - 1)) * 2 - 1) * RIDGE_RADIUS_METERS;
              ridgeRay.set(new THREE.Vector3(ox, oy, 1e5), down);
              const hs = ridgeRay.intersectObject(houseModel, true);
              if (hs.length && hs[0].point.z <= ceiling) {
                hitZs.push(hs[0].point.z);
              }
            }
          }
          if (hitZs.length) {
            hitZs.sort((p, q) => p - q);
            const ri = Math.min(
              hitZs.length - 1,
              Math.max(0, Math.round((hitZs.length - 1) * RIDGE_PERCENTILE))
            );
            modelRidgeZ = hitZs[ri];
            modelTargetZ = modelRidgeZ;
            roofReferenceHeight = meshRidgeHeight;
            placementMode = "ridge-match";
          }
        }

        // Small uniform nudge: ridge-match lands on the footprint but seats a hair
        // low (the smoothed mesh ridge still sits just under the model ridge).
        // Bigger = higher. Keep small -- this is a global trim, not a per-house fix.
        const LIDAR_RIDGE_RAISE_METERS = 0.75;
        anchorZ = modelTargetZ - roofReferenceHeight + LIDAR_RIDGE_RAISE_METERS;

        // DIAGNOSTIC: placementMode should read "ridge-match". ridgeLift = how much
        // higher the ridge match seats vs the old centre pin (big on steep roofs,
        // ~0 on flat ones).
        console.log("[LIDAR Z PLACEMENT]", {
          placementMode,
          modelRoofZ,
          modelRidgeZ,
          modelRoofSource,
          centroidHeight,
          meshCentreHeight,
          meshRidgeHeight,
          ridgeLift:
            modelRidgeZ != null && meshCentreHeight != null
              ? modelTargetZ - roofReferenceHeight - (modelRoofZ - meshCentreHeight)
              : null,
          raise: LIDAR_RIDGE_RAISE_METERS,
          anchorZ,
          centerLat: lidarCenterLat,
          centerLng: lidarCenterLng,
        });
      }
    }
    

    let baseOffsetX = anchorX;
    let baseOffsetY = anchorY;
    let baseOffsetZ = anchorZ;

    if (savedLidarPos) {
      const savedX = toFiniteNumber(savedLidarPos.x, 0);
      const savedY = toFiniteNumber(savedLidarPos.y, 0);
      const savedZ = toFiniteNumber(savedLidarPos.z, null);
      const useAnchorOffset =
        autoAnchorWorld &&
        savedLidarPositionSpace !== "world";

      if (useAnchorOffset) {
        // In georeferenced mode, persisted LiDAR drag is saved as offset from the
        // map anchor so XY edits survive reload while keeping auto-anchor baseline.
        baseOffsetX = anchorX + savedX;
        baseOffsetY = anchorY + savedY;
        // Same for Z: the stored value is the user's vertical nudge relative to the
        // computed roof-seat, so re-applying it on top of the freshly recomputed
        // anchorZ keeps the drag while still tracking the current roof geometry.
        if (savedZ != null) baseOffsetZ = anchorZ + savedZ;
      } else {
        // For non-georeferenced mode (or explicit world-space payload), use absolute world position.
        baseOffsetX = savedX;
        baseOffsetY = savedY;
        if (savedZ != null) baseOffsetZ = savedZ;
      }
    }

    terrainMesh.position.set(baseOffsetX, baseOffsetY, baseOffsetZ);
    arrowControl.position.x += baseOffsetX;
    arrowControl.position.y += baseOffsetY;
    arrowControl.position.z += baseOffsetZ;
    syncFlushBaseToCurrentTransform(terrainMesh);
    syncFlushBaseToCurrentTransform(arrowControl);

    terrainMesh.userData.lidarMeta = {
      ...(terrainMesh.userData.lidarMeta || {}),
      anchor: {
        mode: autoAnchorWorld ? "map-georeferenced" : "origin",
        x: anchorX,
        y: anchorY,
        z: anchorZ,
        centerLat: lidarCenterLat,
        centerLng: lidarCenterLng,
      },
    };

    // Add to scene
    sceneManager.scene.add(terrainMesh);
    sceneManager.scene.add(arrowControl);
    updateLidarPanelOcclusion(terrainMesh);

	//dispatch event to notify LiDAR mesh is ready
	window.dispatchEvent(new CustomEvent("lidarMeshReady", { detail: { meta, anchor: terrainMesh.userData.lidarMeta.anchor } }));


    terrainMesh.visible = true;
    arrowControl.visible = true;

    // --------- DRAG LOGIC ----------
    // LiDAR used to listen for mouse events directly on the canvas while the
    // editor listened for the same events on window. Both flows then started on
    // the first drag frame: the editor could select/rebuild its selection state
    // just as LiDAR disabled OrbitControls. That was the visible initial flicker.
    // Pointer capture keeps one drag owner from press through release, including
    // when the pointer leaves the canvas.
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const dragPlane = new THREE.Plane();
    const dragStartWorld = new THREE.Vector3();
    const dragWorldPoint = new THREE.Vector3();
    const dragMovement = new THREE.Vector3();
    let isDragging = false;
    let dragType = null;
    let dragStartY = 0;
    let initialMeshX = terrainMesh.position.x;
    let initialMeshY = terrainMesh.position.y;
    let initialMeshZ = terrainMesh.position.z;
    let initialArrowX = arrowControl.position.x;
    let initialArrowY = arrowControl.position.y;
    let initialArrowZ = arrowControl.position.z;
    let dragDidChangeTransform = false;

    const canvas = sceneManager.renderer.domElement;

    const setRayFromPointer = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, sceneManager.camera);
    };

    const finishDrag = () => {
      if (!isDragging) return;

      if (dragType === "arrow") {
        arrowControl.children.forEach((child) => {
          if (child.material) {
            if (child.name === "arrowShaft") child.material.color.setHex(0xff6600);
            if (child.name === "arrowHead") child.material.color.setHex(0xff3300);
          }
        });
      }

      canvas.style.cursor = "default";
      isDragging = false;
      dragType = null;

      if (sceneManager.controls) sceneManager.controls.enabled = true;

      if (dragDidChangeTransform) {
        syncFlushBaseToCurrentTransform(terrainMesh);
        syncFlushBaseToCurrentTransform(arrowControl);
        dispatchLidarGeometryChanged(terrainMesh, arrowControl, "drag-end");
      }
      dragDidChangeTransform = false;
    };

    const onPointerDown = (event) => {
      if (event.button !== 0 || !event.isPrimary) return;

      if (!terrainMesh.visible || !arrowControl.visible) return;

      setRayFromPointer(event);

      // Do not raycast the complete scene here. A dense LiDAR mesh was being
      // tested once through every scene child and then tested again below. That
      // synchronous duplicate work delayed the first visible drag frame.
      const arrowIntersects = raycaster.intersectObjects(arrowControl.children, true);
      const meshIntersects = raycaster.intersectObject(terrainMesh);
      const arrowHit = arrowIntersects[0];
      const meshHit = meshIntersects[0];
      if (!arrowHit && !meshHit) return;

      event.preventDefault();

      // Keep the control nearest to the cursor in front when its projected
      // geometry overlaps the terrain surface.
      if (!meshHit || (arrowHit && arrowHit.distance <= meshHit.distance)) {
        isDragging = true;
        dragType = "arrow";
        dragDidChangeTransform = false;
        dragStartY = event.clientY;
        initialMeshZ = terrainMesh.position.z;
        initialArrowZ = arrowControl.position.z;

        arrowControl.children.forEach((child) => {
          if (child.material) child.material.color.setHex(0x00ff00);
        });

        if (sceneManager.controls) sceneManager.controls.enabled = false;
        canvas.setPointerCapture?.(event.pointerId);
        return;
      }

      if (meshHit) {
        isDragging = true;
        dragType = "terrain";
        dragDidChangeTransform = false;
        dragStartY = event.clientY;
        initialMeshX = terrainMesh.position.x;
        initialMeshY = terrainMesh.position.y;
        initialArrowX = arrowControl.position.x;
        initialArrowY = arrowControl.position.y;
        dragStartWorld.copy(meshHit.point);
        dragPlane.set(new THREE.Vector3(0, 0, 1), -dragStartWorld.z);

        canvas.style.cursor = "move";

        if (sceneManager.controls) sceneManager.controls.enabled = false;
        canvas.setPointerCapture?.(event.pointerId);
      }
    };

    // Cursor feedback used to raycast the dense LiDAR terrain on EVERY pointermove.
    // The mesh carries hundreds of thousands of triangles and three.js tests them
    // one by one on the CPU, so an orbit/pan -- which streams pointermove at the
    // mouse's full poll rate -- burned its whole frame budget in that raycast. That
    // is why the scene moved smoothly WITHOUT LiDAR and stuttered with it. Three
    // guards now: skip entirely while a camera gesture owns the pointer, coalesce
    // to one test per frame capped at HOVER_MIN_INTERVAL_MS, and reject with the
    // world bounding box before any triangle is touched.
    // The interval adapts to how expensive this particular terrain is: a Google
    // Solar DSM can reach 1100x1100 cells (~2.4M triangles) and three.js has no
    // acceleration structure, so one pick there costs far more than one on a small
    // mesh. Spending at most ~1/4 of the time budget on picking keeps the cursor
    // responsive on light meshes without letting a heavy one own the main thread.
    const HOVER_MIN_INTERVAL_MS = 50;
    const HOVER_MAX_INTERVAL_MS = 400;
    const HOVER_DUTY_FACTOR = 4;
    const terrainWorldBox = new THREE.Box3();
    let hoverFrame = null;
    let pendingHoverPoint = null;
    let lastHoverTestAt = 0;
    let hoverIntervalMs = HOVER_MIN_INTERVAL_MS;

    if (!geometry.boundingBox) geometry.computeBoundingBox();

    // Orbit (right button) and pan (middle button) hold a button down for the
    // whole gesture, so a non-zero `buttons` mask means the camera -- or another
    // tool's drag -- owns this pointer and hover feedback is not wanted anyway.
    const isCameraGestureActive = (event) =>
      (event.buttons ?? 0) !== 0 || sceneManager.isCameraRotating === true;

    const updateHoverCursor = (point) => {
      // A drag may have started between the pointermove and this frame; the drag
      // owns the cursor from that point on.
      if (isDragging) return;
      if (!terrainMesh.visible || !arrowControl.visible) return;

      setRayFromPointer(point);

      // Arrow first: a handful of triangles, and it wins over the terrain anyway.
      if (raycaster.intersectObjects(arrowControl.children, true).length > 0) {
        canvas.style.cursor = "ns-resize";
        return;
      }

      let overTerrain = false;
      if (geometry.boundingBox) {
        terrainMesh.updateWorldMatrix(true, false);
        terrainWorldBox
          .copy(geometry.boundingBox)
          .applyMatrix4(terrainMesh.matrixWorld);
        overTerrain =
          raycaster.ray.intersectsBox(terrainWorldBox) &&
          raycaster.intersectObject(terrainMesh).length > 0;
      } else {
        overTerrain = raycaster.intersectObject(terrainMesh).length > 0;
      }

      canvas.style.cursor = overTerrain ? "move" : "default";
    };

    const scheduleHoverTest = () => {
      if (hoverFrame !== null) return;
      hoverFrame = requestAnimationFrame(() => {
        hoverFrame = null;
        if (!pendingHoverPoint) return;

        const now = performance.now();
        if (now - lastHoverTestAt < hoverIntervalMs) {
          scheduleHoverTest(); // still too soon; retry on the next frame
          return;
        }

        const point = pendingHoverPoint;
        pendingHoverPoint = null;
        lastHoverTestAt = now;
        updateHoverCursor(point);

        hoverIntervalMs = THREE.MathUtils.clamp(
          (performance.now() - now) * HOVER_DUTY_FACTOR,
          HOVER_MIN_INTERVAL_MS,
          HOVER_MAX_INTERVAL_MS
        );
      });
    };

    const cancelPendingHoverTest = () => {
      if (hoverFrame !== null) {
        cancelAnimationFrame(hoverFrame);
        hoverFrame = null;
      }
      pendingHoverPoint = null;
    };

    const onPointerMove = (event) => {
      if (!event.isPrimary) return;

      if (!isDragging) {
        if (!terrainMesh.visible || !arrowControl.visible) return;

        if (isCameraGestureActive(event)) {
          cancelPendingHoverTest();
          return;
        }

        // Keep only the latest position: intermediate pointermove samples are
        // worthless for a cursor that is redrawn once per frame.
        pendingHoverPoint = { clientX: event.clientX, clientY: event.clientY };
        scheduleHoverTest();
        return;
      }

      if (!terrainMesh.visible || !arrowControl.visible) {
        isDragging = false;
        dragType = null;
        canvas.style.cursor = "default";
        if (sceneManager.controls) sceneManager.controls.enabled = true;
        return;
      }

      event.preventDefault();

      if (dragType === "arrow") {
        const deltaY = event.clientY - dragStartY;
        const sensitivity = 0.1;
        const zOffset = -deltaY * sensitivity;

        terrainMesh.position.z = initialMeshZ + zOffset;
        arrowControl.position.z = initialArrowZ + zOffset;
        updateLidarPanelOcclusion(terrainMesh);

        if (Math.abs(zOffset) > LIDAR_MOVE_EPS) {
          dragDidChangeTransform = true;
        }
      } else if (dragType === "terrain") {
        setRayFromPointer(event);
        if (!raycaster.ray.intersectPlane(dragPlane, dragWorldPoint)) return;

        const movement = dragMovement.subVectors(dragWorldPoint, dragStartWorld);

        terrainMesh.position.x = initialMeshX + movement.x;
        terrainMesh.position.y = initialMeshY + movement.y;

        arrowControl.position.x = initialArrowX + movement.x;
        arrowControl.position.y = initialArrowY + movement.y;

        if (Math.abs(movement.x) > LIDAR_MOVE_EPS || Math.abs(movement.y) > LIDAR_MOVE_EPS) {
          dragDidChangeTransform = true;
        }
      }
    };

    const onPointerUp = (event) => {
      if (!event.isPrimary) return;
      finishDrag();
    };

    // Compatibility mouse events follow pointer events. Stop them at the canvas
    // while LiDAR owns a pointer so the window-level editor handlers cannot
    // start a second selection/panel drag for the same gesture.
    const suppressEditorMouseEvent = (event) => {
      if (!isDragging) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", finishDrag);
    canvas.addEventListener("lostpointercapture", finishDrag);
    canvas.addEventListener("mousedown", suppressEditorMouseEvent, true);
    canvas.addEventListener("mousemove", suppressEditorMouseEvent, true);
    canvas.addEventListener("mouseup", suppressEditorMouseEvent, true);

    const removeInteractions = () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", finishDrag);
      canvas.removeEventListener("lostpointercapture", finishDrag);
      canvas.removeEventListener("mousedown", suppressEditorMouseEvent, true);
      canvas.removeEventListener("mousemove", suppressEditorMouseEvent, true);
      canvas.removeEventListener("mouseup", suppressEditorMouseEvent, true);
      cancelPendingHoverTest();
      finishDrag();
      canvas.style.cursor = "default";
    };
    detachLidarInteractions = removeInteractions;

    // store cleanup (optional)
    terrainMesh.userData.__lidarCleanup = removeInteractions;
  };

  // keep same return shape (cleanup) as before
  return () => {
    jobId++; // invalidate job
    cleanupLidarSceneArtifacts();
  };
}
